/**
 * CR-7782: one cell of the Call Off Management list, by column id.
 *
 * The table renders the viewer's columns in the viewer's order, so each cell is
 * looked up by id rather than written in a fixed sequence. Split out of
 * CallOffListTable to keep that component under the 500-line limit.
 *
 * @link callOffColumnConfig for the column ids and who is offered which.
 */
import type { CallOff } from '../../../lib/api';
import { Badge } from '../../ui/badge';
import { TableCell } from '../../ui/table';
import { MessageSquare } from 'lucide-react';
import CallOffStatusBadges from '../CallOffStatusBadges';
import {
  DESTINATION_ICONS,
  ASN_STATUS_CONFIG,
  getFillColorClass,
} from './callOffListConfig';

interface CallOffListCellProps {
  columnId: string;
  callOff: CallOff;
  /** The party column names the supplier (customer's and haulier's list). */
  partyIsSupplier: boolean;
  isCustomer: boolean;
  formatDate: (date: string | Date) => string;
}

const dateOr = (value: string | null | undefined, format: (d: string) => string, empty = '-') =>
  (value ? format(value) : empty);

export default function CallOffListCell({
  columnId, callOff: co, partyIsSupplier, isCustomer, formatDate,
}: CallOffListCellProps) {
  switch (columnId) {
    // Reference — carries the unread-message flag (CR-7162), so a
    // communication from the other side is visible on the load itself rather
    // than only inside its Communication tab.
    case 'reference':
      return (
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <span>{co.reference}</span>
            {(co.unread_message_count ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800"
                title={`${co.unread_message_count} unread message${co.unread_message_count === 1 ? '' : 's'} from ${isCustomer ? co.supplier_name : co.customer_name}`}
              >
                <MessageSquare className="h-3 w-3" />
                {co.unread_message_count}
              </span>
            )}
          </div>
        </TableCell>
      );
    case 'party':
      return <TableCell>{partyIsSupplier ? co.supplier_name : co.customer_name}</TableCell>;
    case 'pickup_date':
      return <TableCell>{dateOr(co.pickup_date, formatDate)}</TableCell>;
    case 'haulier_pickup_date':
      return <TableCell>{dateOr(co.haulier_pickup_date, formatDate)}</TableCell>;
    case 'destination': {
      const DestIcon = DESTINATION_ICONS[co.destination_type];
      return (
        <TableCell>
          <div className="flex items-center gap-1.5">
            {DestIcon && <DestIcon className="h-4 w-4 text-muted-foreground" />}
            {co.destination_type === 'mixed' ? (
              <span className="text-purple-700 font-medium">{co.destination_name || 'Mixed Delivery'}</span>
            ) : (
              <span>{co.destination_name || '-'}</span>
            )}
          </div>
        </TableCell>
      );
    }
    case 'fill_percent': {
      const fillVal = parseFloat(co.fill_percent || '0');
      return (
        <TableCell className={`text-right ${getFillColorClass(fillVal)}`}>
          {fillVal.toFixed(1)}%
        </TableCell>
      );
    }
    // Detail Status — call-off lifecycle plus, when an ASN has been raised,
    // the ASN's current stage (CR-6291), and CR-7781 the haulier's Logistics
    // Confirmed.
    case 'detail_status':
      return (
        <TableCell>
          <div className="flex flex-col items-start gap-1">
            <CallOffStatusBadges
              status={co.detail_status}
              logisticsConfirmedAt={co.logistics_confirmed_at}
            />
            {co.asn_status && ASN_STATUS_CONFIG[co.asn_status] && (
              <Badge variant="secondary" className={ASN_STATUS_CONFIG[co.asn_status].color}>
                {ASN_STATUS_CONFIG[co.asn_status].label}
              </Badge>
            )}
          </div>
        </TableCell>
      );
    case 'haulage_cost':
      return <TableCell className="text-right">{co.haulage_cost ?? '—'}</TableCell>;
    case 'agreed_delivery_date':
      return <TableCell>{dateOr(co.haulier_agreed_delivery_date, formatDate, '—')}</TableCell>;
    case 'haulier':
      return <TableCell>{co.haulier_name || '—'}</TableCell>;
    case 'lorry_number':
      return <TableCell>{co.lorry_number || '—'}</TableCell>;
    case 'created_date':
      return (
        <TableCell className="text-muted-foreground text-sm">{dateOr(co.created_at, formatDate)}</TableCell>
      );
    // CR-7732: the call-off's own edit log, which the list carries — not
    // `updated_at`, which the list payload has never included.
    case 'last_edited':
      return (
        <TableCell className="text-muted-foreground text-sm">{dateOr(co.last_edited_at, formatDate)}</TableCell>
      );
    default:
      return <TableCell>-</TableCell>;
  }
}
